import { Text, Code, Badge, Flex, Spinner, Divider, FormattedDateTime } from '@customerio/pluma-components/react';
import type { AnonymousInAppState, SearchResult } from '@/services/auth/types';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { isEmptyString } from '@/utils/string';
import { MetadataRow } from './metadata-row';
import { HtmlPreviewFrame } from './html-preview-frame';

type Props = { result: Extract<SearchResult, { kind: 'anonymous_in_app' }> };

// Partial so an unrecognised state (e.g. a future backend value) falls through to no badge
// rather than crashing — the description and timestamps still render.
const STATE_CONFIG: Partial<Record<AnonymousInAppState, { label: string; color: 'green' | 'gray' | 'yellow' }>> = {
  drafted: { label: 'Draft', color: 'gray' },
  scheduled: { label: 'Scheduled', color: 'gray' },
  starting: { label: 'Starting', color: 'green' },
  running: { label: 'Running', color: 'green' },
  stopping: { label: 'Stopping', color: 'yellow' },
  expired: { label: 'Expired', color: 'gray' },
  stopped: { label: 'Stopped', color: 'gray' },
};

type BodyPreviewProps = { isLoading: boolean; isError: boolean; html: string };

// Loading spinner / error message / rendered HTML, extracted to keep AnonymousInAppPreview's
// cognitive complexity low. Callers only render this once there's something to show.
function BodyPreview({ isLoading, isError, html }: BodyPreviewProps) {
  if (isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" py="200">
        <Spinner />
      </Flex>
    );
  }
  if (isError) {
    return (
      <Text size="xs" color="secondary">
        Failed to load preview
      </Text>
    );
  }
  if (!isEmptyString(html)) {
    return <HtmlPreviewFrame html={html} title="In-app message preview" />;
  }
  return null;
}

export function AnonymousInAppPreview({ result }: Props) {
  const envId = Number(result.other.env_id);
  const broadcastId = Number(result.other.id);

  const { data, isLoading, isError } = useHydraUIAPIQuery({
    queryKey: ['getInAppBroadcastById', { params: { environment_id: envId, in_app_broadcast_id: broadcastId } }],
    queryFn: ({ signal }) =>
      hydraUiApi.inApp.getInAppBroadcastsByInAppBroadcast({
        params: { path: { environment_id: String(envId), in_app_broadcast_id: String(broadcastId) } },
        signal,
      }),
    queryConfig: { enabled: envId > 0 && broadcastId > 0, staleTime: 5 * 60 * 1000 },
  });

  // The message body is the broadcast's default template, rendered to HTML in `body`. Anonymous
  // in-app messages have a single template; fall back to the first template that has a body so a
  // content-less draft (empty body) gracefully shows metadata only. Guard `templates` too — the
  // contract types it as required, but the response isn't runtime-validated.
  const body = data?.templates?.find((template) => !isEmptyString(template.body))?.body ?? '';
  const hasBody = !isEmptyString(body);
  const showPreview = isLoading || isError || hasBody;

  const { state } = result.other;
  const stateConfig = state != null ? STATE_CONFIG[state] : undefined;

  return (
    <>
      {showPreview ? (
        <>
          <BodyPreview isLoading={isLoading} isError={isError} html={body} />
          <Divider mt="100" />
        </>
      ) : null}
      <Flex direction="column" gap="100" mt={showPreview ? '100' : undefined}>
        <MetadataRow label="ID">
          <Code size="xs">{result.other.id}</Code>
        </MetadataRow>
        {stateConfig != null ? (
          <MetadataRow label="Status">
            <Badge color={stateConfig.color}>{stateConfig.label}</Badge>
          </MetadataRow>
        ) : null}
        <MetadataRow label="Created">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.created_at} format="medium" />
          </Text>
        </MetadataRow>
        <MetadataRow label="Updated">
          <Text size="xs">
            <FormattedDateTime className="fs-mask" date={result.other.updated_at} format="medium" />
          </Text>
        </MetadataRow>
      </Flex>
    </>
  );
}
